/**
 * The push client's ignition, and the consumer for a tapped notification.
 *
 * ============================================================================
 *  WHY THIS FILE EXISTS AT ALL
 * ============================================================================
 *
 * Phase 38 shipped a complete push client -- token handling, native listeners,
 * an allowlisted deep-link map, a whole Vuex module -- and then never called any
 * of it. Phase 39's RESEARCH grepped `src/` for `pending_navigation`,
 * `resolveScreenRoute`, `refresh_permission` and `request_permission_and_register`
 * and found only definitions, never a call: nothing dispatched `push/register`,
 * `handle_notification_tap` wrote a value that nothing read, and
 * `resolveScreenRoute` was referenced only from its own tests. No device had ever
 * entered the registry. PUSH-07 was recorded as "code complete -- hardware
 * pending" when it was in fact "code written, never invoked".
 *
 * This file is the missing call site. Everything below is wiring; the behaviour
 * it drives already existed.
 *
 * ============================================================================
 *  WHY IT IS NOT WRITTEN INLINE IN app.vue
 * ============================================================================
 *
 * `src/components/app.vue` imports the route table and the real production
 * store, so mounting it under vitest drags in every page SFC and stands up a
 * second live store. Logic written inside it therefore cannot be unit-tested at
 * all, and the guards below are precisely the kind of logic that has to be.
 *
 * Phase 36 hit exactly this and resolved it the same way: the client route
 * guards live in `src/js/guards.js` rather than in `routes.js`, "because
 * routes.js only has a default export, so guards defined there would be
 * untestable". This follows that precedent rather than inventing a new one.
 *
 * Both functions take the store -- and the router -- as arguments for the same
 * reason. Nothing here reaches for a singleton, so a test can hand them a store
 * it built itself.
 */

import { isPushSupported } from './push';

/**
 * Start this device's push session: refresh the OS permission, and register the
 * device when there is a member to bind the token to.
 *
 * Safe to call repeatedly -- app.vue calls it once when Framework7 is ready and
 * again whenever the member logs in during a session. Repeating it is not a no-op
 * and is not meant to be: it re-reads the OS permission, reconciles the Android
 * channels and re-attaches the four native listeners, all of which are idempotent
 * on the device and all of which are how a partial previous run heals itself. The
 * ONE thing that is skipped when nothing has changed is the network POST of a
 * token the server already has -- see `push/needs_token_post`.
 *
 * ############################################################################
 *  THIS FUNCTION MUST NEVER CAUSE AN OS PERMISSION PROMPT.
 * ############################################################################
 *
 * It runs automatically at app launch, which makes it the one place in the app
 * where an accidental prompt would be uncorrectable. iOS gives an app exactly
 * ONE notification prompt for its lifetime: a member who declines a cold prompt
 * -- fired at launch, with no context, before they have done anything that would
 * explain why the app wants to notify them -- can never be asked again from
 * inside the app. The only remaining path is a trip through the system settings
 * app, which almost nobody takes.
 *
 * That is why the last guard below is `permission === 'granted'` and not
 * `permission !== 'denied'`, and why this function dispatches `push/register`
 * but never `push/request_permission_and_register`. The prompt is asked for in
 * context, after the member's first booking (PREF-05, Plan 39-11) or from the
 * banner on the notification settings screen (Plan 39-08) -- never here.
 *
 * `push/refresh_permission` only READS the current OS permission
 * (`PushNotifications.checkPermissions`), which is a silent query and shows the
 * member nothing. It is dispatched on every call on purpose: the member may have
 * turned notifications off in system settings since the last launch, and the
 * settings screen's banner is rendered from the value this refreshes.
 *
 * This is the single interaction in the phase that cannot be corrected by
 * shipping a fix later, so it is defended by a named test
 * (`it never causes an OS permission prompt`) as well as by this comment.
 *
 * @param {import('vuex').Store} store the app's Vuex store
 * @returns {Promise<boolean>} true only when registration was actually started.
 *   Never throws: this is called fire-and-forget from the app shell's mount, and
 *   an unhandled rejection there is a console error nobody reads at best and a
 *   broken boot at worst.
 */
export async function startPushSession(store) {
  try {
    if (!store) {
      return false;
    }

    // 1. Web, or a native build where the plugin failed to load. Nothing to do,
    //    and not an error -- the same code path runs in the browser during
    //    development.
    if (!isPushSupported()) {
      return false;
    }

    // 2. Anonymous session. NEVER register at app launch for a member who is not
    //    logged in: the token is meaningless until it is bound to a member, the
    //    API binds it from the authenticated principal, and POSTing it without a
    //    session would just 401. The isLoggedIn watcher in app.vue is what picks
    //    this up the moment the member does log in.
    if (!store.getters['current_user/isLoggedIn']) {
      return false;
    }

    // 3. An admin is impersonating a member. Do not bind the admin's phone to
    //    the impersonated member's account -- every notification meant for that
    //    member would then land on a device they have never seen, permanently,
    //    long after the impersonation session ended.
    //
    //    `send_token_to_api` already refuses this at the store level
    //    (src/store/modules/push.js), and the API refuses it again with a 403.
    //    This is deliberate defence in depth rather than a duplicate: refusing
    //    here means the whole registration sequence -- including Android channel
    //    creation, which writes permanent per-device state -- never runs on an
    //    admin's phone in a member's name. The two guards stop different amounts
    //    of work.
    if (store.state.current_user && store.state.current_user.origin_user) {
      return false;
    }

    // 3b. PAY OFF A DELETE A PREVIOUS LOGOUT COULD NOT DELIVER (finding C1).
    //
    //     A logout whose DELETE /push-tokens failed -- the member was offline,
    //     which is exactly when the handset is most likely to change hands --
    //     leaves a tombstone in the push module. This is the first moment it can
    //     be paid off, because that DELETE is an authenticated call and the three
    //     guards above are what establish there is a real, non-impersonated
    //     session to make it with.
    //
    //     IT SITS ABOVE `push/register`, NOT BELOW IT, AND THE ORDER IS THE WHOLE
    //     POINT. The OS does not rotate a platform token because somebody logged
    //     out, so this handset usually comes back holding the very token the
    //     tombstone names. Draining after registration would therefore delete the
    //     row this same launch had just created, and the member would receive
    //     nothing for the whole session -- a worse bug than the one being fixed.
    //     Awaited for that reason: a fire-and-forget DELETE could land after the
    //     POST.
    //
    //     THE ORDER MATTERS TWICE OVER SINCE FINDING F2. When the tombstone belongs
    //     to a DIFFERENT member -- the handset changed hands -- the retry is no
    //     longer a DELETE at all. It is the OS-level `unregister()`, because
    //     DELETE /push-tokens is scoped to the authenticated member server-side and
    //     answers 204 even when it deleted nothing, so sending it as the wrong
    //     principal would clear the tombstone without removing anything. Revoking
    //     at the OS AFTER `push/register` would tear down the registration THIS
    //     member had just established for themselves.
    //
    //     It also sits above the permission check on purpose. Whether THIS member
    //     has granted notification permission says nothing about whether a
    //     PREVIOUS session left a row behind, and a handset whose member has
    //     notifications switched off is still perfectly able to owe that DELETE --
    //     or to be carrying the previous owner's live OS registration.
    //
    //     Non-blocking: `retry_pending_unregistration` never rejects, resolves
    //     false when there is nothing outstanding or the retry failed, and raises
    //     no toast. A member logging in on the same bad connection that created
    //     the tombstone still gets a working device; the row waits one more
    //     launch.
    await store.dispatch('push/retry_pending_unregistration');

    // 4. THERE IS DELIBERATELY NO "already registered, stop here" GUARD AT THIS
    //    POINT. Findings B3 and C5 -- one defect, found independently by two
    //    reviewers -- were exactly that guard, and re-adding it would restore the
    //    bug in full. Read this before you optimise a repeat launch.
    //
    //    What used to sit here returned early when `push/is_registered` was true.
    //    That flag records only that ONE HISTORICAL POST was once accepted; it is
    //    mirrored into localStorage with the rest of the store and restored on
    //    every cold launch. It says nothing about whether the server row still
    //    exists -- the server's reaper deletes tokens the platform rejects -- and
    //    nothing about whether the platform has since rotated the token.
    //
    //    The damage was not "one POST was skipped". The guard sat in front of
    //    `push/register`, and register() is what creates the Android notification
    //    channels AND attaches ALL FOUR native listeners: `registration`,
    //    `registrationError`, `pushNotificationReceived` and
    //    `pushNotificationActionPerformed` (src/js/push.js:168-201). So a handset
    //    that restored `registered: true` attached NO LISTENERS AT ALL. A rotated
    //    token arrives on `registration` and there was nothing there to hear it,
    //    so the replacement was never sent and the server kept pushing to a dead
    //    token. A tapped notification arrives on
    //    `pushNotificationActionPerformed` and there was nothing there to route
    //    it. Channels were never reconciled, so a first run that failed halfway
    //    stayed broken despite the comments promising it would heal. Push was
    //    silently dead, on a handset reporting itself as registered, until the
    //    member happened to log out.
    //
    //    The redundant work the old guard was reaching for is real, but it is
    //    exactly ONE request: re-POSTing a token the server already has. That is
    //    now skipped where it can be judged properly -- inside the store, against
    //    the token the claim was actually recorded for
    //    (`push/needs_token_post`) -- rather than here, where the only thing in
    //    scope is a boolean that cannot tell a rotated token from an unchanged
    //    one. Everything the device needs re-established on every launch is
    //    idempotent: recreating an Android channel with identical values is a
    //    no-op, and startRegistration() removes the listeners before it attaches
    //    them, so they cannot stack.

    await store.dispatch('push/refresh_permission');

    // Read the getter rather than the dispatch's return value so this asserts on
    // the state the rest of the app renders from, not on a value in flight.
    if (store.getters['push/permission'] !== 'granted') {
      // Permission is 'prompt' or 'denied'. Both end the launch path here. See
      // the block comment above: asking is somebody else's job, in context.
      return false;
    }

    await store.dispatch('push/register');

    // Same rule as the permission check: report the state the rest of the app can
    // see. `is_session_registered` is the PER-PROCESS fact -- this process has its
    // listeners attached -- and never the persisted server-state claim, which is
    // the confusion this whole change exists to remove.
    return store.getters['push/is_session_registered'];
  } catch (e) {
    // Deliberately not logged. A failure here costs the member notifications,
    // never the app, and the failure that matters (a native registration error)
    // is already recorded in `push.registration_error` for diagnosis.
    return false;
  }
}

/**
 * Decide, once per device, whether the login path should show the permission
 * explainer sheet (OWNER DECISION 2026-08-20).
 *
 * Phase 39 asked only after the first booking (PREF-05). That left a member who
 * updates to the push-capable build and simply logs in never asked at all. So
 * app.vue now offers the SAME explainer sheet right after the ignition runs for
 * a logged-in member -- the OS dialog is still reached only through the sheet's
 * accept button, so iOS's one shot is protected exactly as before.
 *
 * Marked shown on OFFER, not on accept, for the same reason calendar.vue does it:
 * a member who tapped "Niet nu" has been asked, and the settings-screen banner
 * (PREF-04) is their way back. Never nags.
 *
 * @param {import('vuex').Store} store
 * @returns {boolean} true when the caller should open the sheet now
 */
export function offerPermissionPromptAtLogin(store) {
  try {
    if (!store) {
      return false;
    }

    if (!store.getters['current_user/isLoggedIn']) {
      return false;
    }

    if (store.state.current_user && store.state.current_user.origin_user) {
      return false;
    }

    if (!store.getters['push/should_offer_permission_prompt']) {
      return false;
    }

    store.dispatch('push/mark_permission_prompt_shown');

    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Act on a tapped notification: take the pending destination out of the store
 * and navigate to it, exactly once.
 *
 * The destination always comes from `push/consume_pending_navigation`, which
 * resolves the payload's `screen` through the SCREEN_ROUTES allowlist. The
 * payload arrives from outside the app and the WebView it would be steering
 * holds a bearer token that opens the gym's door, so the allowlist is a real
 * control and not hygiene: an unknown or hostile `screen` resolves to home
 * rather than being followed. This function must never be given a path from
 * anywhere else.
 *
 * WHY IT REFUSES TO CONSUME WITHOUT A ROUTER. A tap can cold-start the app:
 * Capacitor queues `pushNotificationActionPerformed` until a listener is
 * attached, so the tap that LAUNCHED the app is delivered during boot, possibly
 * before Framework7 has built its views. Consuming in that window would clear
 * the store value and then have nowhere to send it -- the member taps a
 * notification and lands on the home screen for no visible reason. Returning
 * early WITHOUT consuming is strictly better: the pending value is exactly what
 * survives until the router exists, and app.vue re-runs this the moment its
 * `f7Ready` flag flips true.
 *
 * @param {import('vuex').Store} store the app's Vuex store
 * @param {object|null} router the main view's Framework7 router, if it exists yet
 * @returns {Promise<string|null>} the allowlisted path that was navigated to, or
 *   null when nothing was pending or nothing could be done with it. Never throws.
 */
export async function consumeNotificationNavigation(store, router) {
  try {
    if (!store) {
      return null;
    }

    // Not ready, or not a router at all. Leave the value in the store.
    if (!router || typeof router.navigate !== 'function') {
      return null;
    }

    const path = await store.dispatch('push/consume_pending_navigation');

    // Nothing was tapped. By far the common case -- this runs on every change to
    // the watched key, including the one that clears it.
    if (!path) {
      return null;
    }

    router.navigate(path);

    return path;
  } catch (e) {
    // If navigate() itself threw, the pending value has already been cleared and
    // the tap is lost. That is the accepted trade: the alternative is writing the
    // destination back, and a watcher that re-fires on every write would turn one
    // broken navigation into a loop the member cannot escape. A throw here means
    // an allowlisted path the router cannot resolve, which is a routing bug
    // rather than a runtime condition.
    return null;
  }
}
